import React, { useCallback } from "react";

import { copy } from "shared/assets";
import { Card, Title, Table, TableColumn } from "shared/components";
import { Spacer } from "shared/layout";
import { ICardTheme, IStatus, ITheme, IFormButtons } from "shared/types";
import { IStageCapacity, IStageCapacityType } from "Venues/types";
import { capacityTypeOptions } from "Venues/utils";
import { StageCapacityForm } from "./components/StageCapacityForm";

const { stageProperties, createStage, actions: actionsCopy } = copy;

const buttons: IFormButtons = {
  primary: {
    children: createStage.capacities.cta,
    size: "m",
    "data-testid": "create_capacity_button",
  },
};

interface StageCapacityProps {
  capacities: IStageCapacity[];
  isSubmitting?: boolean;
  hasError?: boolean;
  resetForm?: boolean;
  addCapacity: (capacity: IStageCapacity) => void;
  handleDelete: (capacity: IStageCapacity) => void;
  handleEdit: (capacity: IStageCapacity) => void;
}

export const StageCapacity: React.FC<StageCapacityProps> = ({
  capacities,
  addCapacity,
  handleEdit,
  handleDelete,
  ...rest
}) => {
  const getRowActions = useCallback(
    (id: string) => {
      const getSelectedCapacityFromId = (
        id: string
      ): IStageCapacity | undefined => {
        return capacities.find((capacity) => capacity.id === id);
      };
      return [
        {
          label: actionsCopy.edit,
          dataTestId: "edit",
          handleClick: () => {
            const capacity = getSelectedCapacityFromId(id);
            if (capacity) handleEdit(capacity);
          },
        },
        {
          label: actionsCopy.delete,
          dataTestId: "delete",
          handleClick: () => {
            const capacity = getSelectedCapacityFromId(id);
            if (capacity) handleDelete(capacity);
          },
          status: IStatus.error,
        },
      ];
    },
    [handleDelete, handleEdit, capacities]
  );

  return (
    <>
      <Card theme={ICardTheme.secondary}>
        <Spacer size="xl">
          <Spacer size="m">
            <Title title={stageProperties.capacities.title} asComponent="h2" />
            <StageCapacityForm
              handleSubmit={addCapacity}
              buttons={buttons}
              inlineForm
              {...rest}
            />
          </Spacer>
          <Spacer size="m">
            <Title
              title={createStage.capacities.list.title}
              asComponent="h3"
              size="s"
            />
            <Table
              columns={columns}
              data={capacities}
              getRowActions={getRowActions}
              emptyCopy={createStage.capacities.list.empty}
              theme={ITheme.secondary}
            />
          </Spacer>
        </Spacer>
      </Card>
    </>
  );
};

const { properties } = stageProperties.capacities;

const columns: TableColumn[] = [
  {
    Header: properties.title.title,
    accessor: "title",
  },
  {
    Header: properties.capacity.title,
    accessor: "capacity",
    Cell: ({ value }) => value.toLocaleString(),
  },
  {
    Header: properties.capacityType.title,
    accessor: "type",
    Cell: ({ value }: { value: keyof typeof IStageCapacityType }) =>
      capacityTypeOptions[value],
  },
];
